import React, { forwardRef, useMemo, useCallback } from "react";
import { Graphics } from "@pixi/react";
import { Settings, STAGE_DIMENSIONS } from "../../../settings";
import { FEATURES_CONFIG } from "../../../featuresConfig";

// Mask vertical padding as a fraction of symbol height (0.02 ≈ 4px at the
// native 214).
const MASK_PADDING_MULTIPLIER = 0.02;

/**
 * Clips the reels to the visible rows (plus a little padding) so symbols
 * spinning in above and below the window stay hidden. Full stage width: only
 * top and bottom are masked. The optional yellow outline is a dev aid
 * (DEV_TOOLS.DEBUG_BORDER).
 */
const ReelSetMask = forwardRef(
  ({ symbolWidth, symbolHeight, dividerDimensions }, ref) => {

    // No horizontal clipping — full stage width. Only top/bottom are masked.
    const maskDimensions = useMemo(() => {
      const padding = Math.round(symbolHeight * MASK_PADDING_MULTIPLIER);
      return {
        x: -STAGE_DIMENSIONS.MAX_WIDTH / 2,
        y: -(symbolHeight * Settings.SYMBOLS_VISIBLE) / 2 - padding,
        width: STAGE_DIMENSIONS.MAX_WIDTH,
        height: symbolHeight * Settings.SYMBOLS_VISIBLE + padding * 2,
      };
    }, [symbolHeight]);

    // Memoize draw function to prevent recreation on every render
    const draw = useCallback(
      (g) => {
        g.clear();
        g.beginFill(0x000000, 1);
        g.drawRect(
          maskDimensions.x,
          maskDimensions.y,
          maskDimensions.width,
          maskDimensions.height
        );
        g.endFill();
      },
      [maskDimensions]
    );

    // Debug border draw function
    const drawDebugBorder = useCallback(
      (g) => {
        g.clear();
        g.lineStyle(1, 0xffff00, 1);
        g.drawRect(
          maskDimensions.x,
          maskDimensions.y,
          maskDimensions.width,
          maskDimensions.height
        );
      },
      [maskDimensions]
    );

    return (
      <>
        <Graphics
          ref={ref}
          name="overflow_mask"
          draw={draw}
        />
        {FEATURES_CONFIG.DEV_TOOLS.DEBUG_BORDER && (
          <Graphics
            name="debug_border_reelset"
            draw={drawDebugBorder}
            zIndex={100}
          />
        )}
      </>
    );
  }
);

export default ReelSetMask;
